import { useEffect } from "react";
import { X } from "lucide-react";
import { F, T } from "../../theme/tokens";

// A centred dialog: title bar with a close button, a scrolling body and a pinned footer.
// Escape and a click on the dimmed backdrop close it. On phones it is nearly full width
// and never taller than the screen (the body scrolls, the footer stays put).
export function Modal({ title, onClose, children, footer, width = 480 }: {
  title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; width?: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label={title}
        style={{ background: T.surface, borderRadius: 14, width: `min(${width}px, 100%)`, maxHeight: "calc(100dvh - 32px)", display: "flex", flexDirection: "column", fontFamily: F, boxShadow: "0 24px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", padding: 4 }}><X size={16} /></button>
        </div>
        <div style={{ padding: 22, overflowY: "auto", flex: 1 }}>{children}</div>
        {footer && <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", padding: "14px 22px", borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>{footer}</div>}
      </div>
    </div>
  );
}

// ── Form bits shared by dialogs ───────────────────────────────────────────────

export function FieldLabel({ children, required, hint }: { children: React.ReactNode; required?: boolean; hint?: string }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text, fontFamily: F }}>
        {children}{required && <span style={{ color: "#D1004B" }}> *</span>}
      </label>
      {hint && <p style={{ fontSize: 11, color: T.muted, fontFamily: F, margin: "2px 0 0" }}>{hint}</p>}
    </div>
  );
}

export const inputStyle: React.CSSProperties = {
  width: "100%", border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: F,
  color: T.text, background: T.surface, outline: "none", boxSizing: "border-box",
};
